import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { ScrollArea } from '../../ui/scroll-area';
import { Plus, Table2 } from 'lucide-react';
import { ParsedWorksheet } from './worksheet';

interface MapleStatsDataSheetProps {
  worksheet: ParsedWorksheet;
  dataInput: string;
  delimiterMode: string;
  selectedColumnId?: string;
  onDataInputChange: (value: string) => void;
  onDelimiterModeChange: (value: string) => void;
  onSelectedColumnIdChange: (value: string) => void;
}

const DEFAULT_COLUMN_COUNT = 8;
const DEFAULT_DATA_ROW_COUNT = 18;

const formatNumber = (value: number) => {
  if (!Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1000 || Math.abs(value) < 0.01) {
    return value.toExponential(3);
  }
  return value.toFixed(3);
};

const toColumnLabel = (index: number) => `C${index + 1}`;

const getDelimiter = (delimiterMode: string) => {
  if (delimiterMode === 'tab') return '\t';
  if (delimiterMode === 'space') return ' ';
  return ',';
};

const splitPastedTable = (text: string) => {
  const trimmed = text.replace(/\r/g, '').replace(/\n$/, '');
  if (!trimmed) return [['']];

  return trimmed.split('\n').map((line) => {
    if (line.includes('\t')) return line.split('\t');
    if (line.includes(',')) return line.split(',');
    return [line];
  });
};

const buildGridFromWorksheet = (worksheet: ParsedWorksheet, minimumColumnCount: number, minimumDataRowCount: number) => {
  const columnCount = Math.max(worksheet.columnCount || 0, minimumColumnCount, DEFAULT_COLUMN_COUNT);
  const dataRowCount = Math.max(worksheet.rowCount || 0, minimumDataRowCount, DEFAULT_DATA_ROW_COUNT);
  const headerRow = Array.from({ length: columnCount }, (_, index) => worksheet.columnNames[index] ?? toColumnLabel(index));
  const dataRows = Array.from({ length: dataRowCount }, (_, rowIndex) =>
    Array.from({ length: columnCount }, (_, columnIndex) => worksheet.rows[rowIndex]?.[columnIndex] ?? ''),
  );

  return { headerRow, dataRows, columnCount, dataRowCount };
};

const serializeGrid = (headerRow: string[], dataRows: string[][], delimiterMode: string) => {
  const effectiveColumnCount = Math.max(
    headerRow.length,
    ...dataRows.map((row) => row.findLastIndex((cell) => cell.trim() !== '') + 1),
    1,
  );

  const trimmedHeader = Array.from({ length: effectiveColumnCount }, (_, index) => headerRow[index] ?? toColumnLabel(index));
  const normalizedRows = dataRows
    .map((row) => Array.from({ length: effectiveColumnCount }, (_, index) => row[index] ?? ''))
    .map((row) => {
      const lastNonEmptyIndex = row.findLastIndex((cell) => cell.trim() !== '');
      if (lastNonEmptyIndex === -1) return [] as string[];
      return row.slice(0, Math.max(lastNonEmptyIndex + 1, 1));
    });

  const lastNonEmptyDataRow = normalizedRows.findLastIndex((row) => row.some((cell) => cell.trim() !== ''));
  const trimmedDataRows = lastNonEmptyDataRow === -1 ? [] : normalizedRows.slice(0, lastNonEmptyDataRow + 1);

  if (!trimmedHeader.some((cell) => cell.trim() !== '') && trimmedDataRows.length === 0) {
    return '';
  }

  const delimiter = getDelimiter(delimiterMode);
  return [trimmedHeader, ...trimmedDataRows].map((row) => row.join(delimiter)).join('\n');
};

export const MapleStatsDataSheet: React.FC<MapleStatsDataSheetProps> = ({
  worksheet,
  dataInput,
  delimiterMode,
  selectedColumnId,
  onDataInputChange,
  onDelimiterModeChange,
  onSelectedColumnIdChange,
}) => {
  const [selectedCell, setSelectedCell] = useState({ row: 0, column: 0 });
  const [minimumColumnCount, setMinimumColumnCount] = useState(DEFAULT_COLUMN_COUNT);
  const [minimumDataRowCount, setMinimumDataRowCount] = useState(DEFAULT_DATA_ROW_COUNT);

  const grid = useMemo(
    () => buildGridFromWorksheet(worksheet, minimumColumnCount, minimumDataRowCount),
    [minimumColumnCount, minimumDataRowCount, worksheet],
  );

  useEffect(() => {
    if (!dataInput.trim()) {
      setMinimumColumnCount(DEFAULT_COLUMN_COUNT);
      setMinimumDataRowCount(DEFAULT_DATA_ROW_COUNT);
      return;
    }

    setMinimumColumnCount((current) => Math.max(current, worksheet.columnCount || 0, DEFAULT_COLUMN_COUNT));
    setMinimumDataRowCount((current) => Math.max(current, worksheet.rowCount || 0, DEFAULT_DATA_ROW_COUNT));
  }, [dataInput, worksheet.columnCount, worksheet.rowCount]);

  useEffect(() => {
    setSelectedCell((current) => ({
      row: Math.min(current.row, grid.dataRowCount),
      column: Math.min(current.column, grid.columnCount - 1),
    }));
  }, [grid.columnCount, grid.dataRowCount]);

  const selectedValue = selectedCell.row === 0
    ? grid.headerRow[selectedCell.column] ?? ''
    : grid.dataRows[selectedCell.row - 1]?.[selectedCell.column] ?? '';

  const selectedColumn = worksheet.numericColumns.find((column) => column.id === selectedColumnId) ?? worksheet.numericColumns[0];

  const worksheetSummary = useMemo(() => {
    if (!selectedColumn?.numericValues.length) return null;
    const values = [...selectedColumn.numericValues].sort((a, b) => a - b);
    const min = values[0];
    const max = values[values.length - 1];
    const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
    const medianIndex = Math.floor(values.length / 2);
    const median = values.length % 2 === 0 ? (values[medianIndex - 1] + values[medianIndex]) / 2 : values[medianIndex];
    return { min, max, mean, median };
  }, [selectedColumn]);

  const commitGrid = (headerRow: string[], dataRows: string[][]) => {
    onDataInputChange(serializeGrid(headerRow, dataRows, delimiterMode));
  };

  const updateCell = (row: number, column: number, value: string) => {
    const nextHeader = [...grid.headerRow];
    const nextRows = grid.dataRows.map((dataRow) => [...dataRow]);

    if (row === 0) {
      nextHeader[column] = value;
    } else {
      if (!nextRows[row - 1]) {
        nextRows[row - 1] = Array.from({ length: grid.columnCount }, () => '');
      }
      nextRows[row - 1][column] = value;
    }

    commitGrid(nextHeader, nextRows);
  };

  const handleFormulaBarChange = (value: string) => {
    updateCell(selectedCell.row, selectedCell.column, value);
  };

  const handlePasteAt = (row: number, column: number, text: string) => {
    const pastedRows = splitPastedTable(text);
    const requiredColumnCount = Math.max(grid.columnCount, column + Math.max(...pastedRows.map((pastedRow) => pastedRow.length), 1));
    const requiredDataRowCount = Math.max(grid.dataRowCount, row === 0 ? pastedRows.length - 1 : row - 1 + pastedRows.length);

    setMinimumColumnCount((current) => Math.max(current, requiredColumnCount));
    setMinimumDataRowCount((current) => Math.max(current, requiredDataRowCount));

    const nextHeader = Array.from({ length: requiredColumnCount }, (_, index) => grid.headerRow[index] ?? toColumnLabel(index));
    const nextRows = Array.from({ length: requiredDataRowCount }, (_, rowIndex) =>
      Array.from({ length: requiredColumnCount }, (_, columnIndex) => grid.dataRows[rowIndex]?.[columnIndex] ?? ''),
    );

    pastedRows.forEach((pastedRow, pastedRowIndex) => {
      pastedRow.forEach((value, pastedColumnIndex) => {
        const targetColumn = column + pastedColumnIndex;
        const targetRow = row + pastedRowIndex;
        if (targetRow === 0) {
          nextHeader[targetColumn] = value;
        } else {
          nextRows[targetRow - 1][targetColumn] = value;
        }
      });
    });

    commitGrid(nextHeader, nextRows);
    setSelectedCell({ row, column });
  };

  const addRow = () => {
    setMinimumDataRowCount((current) => current + 1);
    setSelectedCell({ row: grid.dataRowCount + 1, column: selectedCell.column });
  };

  const addColumn = () => {
    setMinimumColumnCount((current) => current + 1);
    setSelectedCell({ row: 0, column: grid.columnCount });
  };

  const selectedCellLabel = `${toColumnLabel(selectedCell.column)}${selectedCell.row === 0 ? ' (header)' : selectedCell.row}`;

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3">
        <div>
          <div className="flex items-center gap-2">
            <Table2 className="h-4 w-4 text-primary" />
            <h2 className="text-xl font-semibold">Data</h2>
          </div>
          <p className="text-sm text-muted-foreground">Paste directly into the grid and use it as the worksheet for all MapleStats modules.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={addRow}><Plus className="mr-1 h-4 w-4" />Row</Button>
          <Button variant="outline" size="sm" onClick={addColumn}><Plus className="mr-1 h-4 w-4" />Column</Button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-4 overflow-hidden xl:grid-cols-[minmax(0,1fr)_320px]">
        <Card className="min-h-0 overflow-hidden">
          <CardHeader className="border-b border-border pb-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-[120px] text-sm font-medium">Selected: {selectedCellLabel}</div>
              <Input value={selectedValue} onChange={(event) => handleFormulaBarChange(event.target.value)} placeholder="Edit the selected cell" className="min-w-[260px] flex-1" />
            </div>
          </CardHeader>
          <CardContent className="min-h-0 h-full p-0">
            <ScrollArea className="h-full min-h-0">
              <div className="min-w-max">
                <table className="w-full border-collapse text-sm">
                  <thead className="sticky top-0 z-20 bg-muted/95 backdrop-blur">
                    <tr>
                      <th className="sticky left-0 z-30 min-w-14 border border-border bg-muted px-2 py-2 text-center text-xs font-semibold text-muted-foreground">#</th>
                      {Array.from({ length: grid.columnCount }, (_, columnIndex) => {
                        const isSelectedColumn = selectedCell.column === columnIndex;
                        return (
                          <th
                            key={`header-${columnIndex}`}
                            className={`min-w-36 border border-border px-2 py-1 ${isSelectedColumn ? 'bg-primary/10' : 'bg-muted'}`}
                          >
                            <input
                              value={grid.headerRow[columnIndex] ?? ''}
                              onChange={(event) => updateCell(0, columnIndex, event.target.value)}
                              onFocus={() => setSelectedCell({ row: 0, column: columnIndex })}
                              onPaste={(event) => {
                                event.preventDefault();
                                handlePasteAt(0, columnIndex, event.clipboardData.getData('text'));
                              }}
                              className="w-full border-0 bg-transparent text-sm font-semibold outline-none"
                            />
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: grid.dataRowCount }, (_, rowIndex) => {
                      const rowNumber = rowIndex + 1;
                      return (
                        <tr key={`row-${rowNumber}`}>
                          <td className="sticky left-0 z-10 border border-border bg-muted/90 px-2 text-center text-xs font-medium text-muted-foreground">{rowNumber}</td>
                          {Array.from({ length: grid.columnCount }, (_, columnIndex) => {
                            const isSelected = selectedCell.row === rowNumber && selectedCell.column === columnIndex;
                            return (
                              <td key={`cell-${rowNumber}-${columnIndex}`} className={`border border-border p-0 ${isSelected ? 'bg-primary/5 ring-1 ring-inset ring-primary' : 'bg-background'}`}>
                                <input
                                  value={grid.dataRows[rowIndex]?.[columnIndex] ?? ''}
                                  onFocus={() => setSelectedCell({ row: rowNumber, column: columnIndex })}
                                  onChange={(event) => updateCell(rowNumber, columnIndex, event.target.value)}
                                  onPaste={(event) => {
                                    event.preventDefault();
                                    handlePasteAt(rowNumber, columnIndex, event.clipboardData.getData('text'));
                                  }}
                                  className="h-10 w-36 border-0 bg-transparent px-3 text-sm outline-none"
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Worksheet controls</CardTitle>
              <CardDescription>Manage how the grid is serialized into MapleStats analysis data.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="maplestats-delimiter-mode">Delimiter mode</Label>
                <Select value={delimiterMode} onValueChange={onDelimiterModeChange}>
                  <SelectTrigger id="maplestats-delimiter-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto detect</SelectItem>
                    <SelectItem value="comma">Comma-separated</SelectItem>
                    <SelectItem value="tab">Tab-delimited</SelectItem>
                    <SelectItem value="space">Space-separated</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="active-analysis-column">Active analysis column</Label>
                <Select value={selectedColumn?.id ?? ''} onValueChange={onSelectedColumnIdChange}>
                  <SelectTrigger id="active-analysis-column"><SelectValue placeholder="Choose a numeric column" /></SelectTrigger>
                  <SelectContent>
                    {worksheet.numericColumns.map((column) => (
                      <SelectItem key={column.id} value={column.id}>{column.name} ({column.numericCount})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-lg border border-border/70 bg-muted/40 p-3 text-sm text-muted-foreground">
                Paste a full table from Excel, MapleSheet, or a CSV export directly into any cell. Tab and newline structure is preserved across the grid.
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Worksheet summary</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-muted/60 p-3"><div className="text-muted-foreground">Rows</div><div className="text-lg font-semibold">{worksheet.rowCount}</div></div>
              <div className="rounded-lg bg-muted/60 p-3"><div className="text-muted-foreground">Columns</div><div className="text-lg font-semibold">{worksheet.columnCount}</div></div>
              <div className="rounded-lg bg-muted/60 p-3"><div className="text-muted-foreground">Numeric columns</div><div className="text-lg font-semibold">{worksheet.numericColumns.length}</div></div>
              <div className="rounded-lg bg-muted/60 p-3"><div className="text-muted-foreground">Headers</div><div className="text-lg font-semibold">{worksheet.hasHeaders ? 'Yes' : 'No'}</div></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Column catalog</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {worksheet.columns.length === 0 ? (
                <p className="text-sm text-muted-foreground">Start typing or paste data into the worksheet grid.</p>
              ) : (
                worksheet.columns.map((column) => (
                  <div key={column.id} className="rounded-lg border border-border/70 p-3">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div className="font-medium">{column.name}</div>
                      <Badge variant="outline">{column.numericCount > 0 ? `${column.numericCount} numeric` : `${column.uniqueValues.length} levels`}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {column.numericCount > 0
                        ? `${column.missingCount} non-numeric cells skipped for numeric analysis`
                        : column.uniqueValues.slice(0, 4).join(', ') || 'No non-empty values'}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Selected column summary</CardTitle>
            </CardHeader>
            <CardContent>
              {worksheetSummary ? (
                <div className="space-y-2 rounded-lg border border-border/70 p-3 text-sm">
                  <div className="flex items-center justify-between"><span className="text-muted-foreground">Mean</span><span className="font-medium">{formatNumber(worksheetSummary.mean)}</span></div>
                  <div className="flex items-center justify-between"><span className="text-muted-foreground">Median</span><span className="font-medium">{formatNumber(worksheetSummary.median)}</span></div>
                  <div className="flex items-center justify-between"><span className="text-muted-foreground">Min</span><span className="font-medium">{formatNumber(worksheetSummary.min)}</span></div>
                  <div className="flex items-center justify-between"><span className="text-muted-foreground">Max</span><span className="font-medium">{formatNumber(worksheetSummary.max)}</span></div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Select a numeric column to inspect its summary statistics.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="rounded-lg border border-border/70 bg-card px-4 py-3 text-xs text-muted-foreground">
        Current worksheet source is synchronized from the sheet grid into MapleStats analysis modules. Stored text length: {dataInput.length} characters.
      </div>
    </div>
  );
};